import React from 'react';
import PropTypes from 'prop-types';
import {
  CardMedia,
  Card,
  CardTitle,
  Button,
  Typography
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import ListsHeader from '../ListsHeader';
import CreateNewList from '../CreateNewList';
import { NAVIGATION_URLS } from '../../../constants/navigationUrls';
import NullViewTexts from '../../../constants/NullViewTexts';

const DESKTOP_BREAKPOINT = 992; // Aligns with SUI @lg container query breakpoint

const DesktopNullView = ({ containerWidth, isDashboardSnapshot }) => {
  // A wide container puts the image at a third of the row and lets the buttons hug their labels.
  // Dashboard Snapshot (Figma "Desktop Null") keeps the narrower layout at any width: the image
  // and the text split the row, and the two buttons split the text column equally.
  const isWideLayout = !isDashboardSnapshot && containerWidth >= DESKTOP_BREAKPOINT;

  const {
    imageAlt, imageUrl, cardTitle, benefits, learnMoreText
  } = NullViewTexts;

  return (
    <Card
      data-component="Lists"
      data-testid="desktop-null-view"
      orientation="horizontal"
      className="sui-h-full"
      // Dashboard Snapshot: no border, like the snapshot's other cards, and no header row, since
      // the tab already names the tool.
      PaperProps={isDashboardSnapshot ? { variant: 'shadow', shadow: 'none' } : undefined}
      CardContentProps={{
        className: classNames(
          'sui-flex',
          'sui-flex-col',
          'sui-pt-4',
          'sui-px-4',
          'sui-pb-4',
          'sui-h-full',
          'sui-w-full'
        )
      }}
    >
      <div className="sui-flex sui-gap-4 sui-h-full sui-w-full">
        <div className="sui-flex sui-flex-col sui-flex-1 sui-h-full">
          {!isDashboardSnapshot && <ListsHeader />}
          <div data-testid="null-view-content" className="sui-flex sui-flex-col sui-flex-1">
            <CardTitle
              headerVariant="h3"
              headerWeight="display"
              header={<span style={{ whiteSpace: 'pre-line' }}>{cardTitle.join('\n')}</span>}
            />
            <div className="sui-mt-2">
              <Typography>
                {benefits.map((sentence, i) => (
                  <React.Fragment key={`benefit-${i}`}>
                    {sentence}{i < benefits.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </div>
            <div className="sui-mt-auto sui-pt-4">
              <div className="sui-flex sui-gap-4">
                <div className={isWideLayout ? 'sui-flex-none' : 'sui-flex-1'}>
                  <Button
                    variant="secondary"
                    fullWidth={!isWideLayout}
                    href={NAVIGATION_URLS.LEARN_MORE}
                  >
                    {learnMoreText}
                  </Button>
                </div>
                <div className={isWideLayout ? 'sui-flex-none' : 'sui-flex-1'}>
                  <CreateNewList variant="button" fullWidth={!isWideLayout} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={classNames(
          'sui-hidden',
          '@lg:sui-block',
          'sui-overflow-hidden',
          { 'sui-basis-1/3': isWideLayout, 'sui-flex-1': !isWideLayout, 'sui-rounded-md': isDashboardSnapshot }
        )}
        >
          <CardMedia
            src={imageUrl}
            alt={imageAlt}
            className="sui-w-full sui-h-full sui-transform sui-object-cover sui-object-center"
          />
        </div>
      </div>
    </Card>
  );
};

DesktopNullView.propTypes = {
  containerWidth: PropTypes.number,
  isDashboardSnapshot: PropTypes.bool,
};

DesktopNullView.defaultProps = {
  containerWidth: 0,
  isDashboardSnapshot: false,
};

export default DesktopNullView;
